import type { SkillExitSlipProgress } from '../models/ExitSlip';
import type { SkillExitSlipBadgeSummary } from './StudentDashboardAux/skillExitSlipProgress';
import { EXIT_SLIP_PASSING_RATIO } from './StudentDashboardAux/exitSlipOutcome';

/**
 * What the skill page (/skill/:skillCode) says about a skill's exit tickets, on top of what the
 * unit-path card already says about it on the dashboard.
 *
 * Deliberately built on the same SkillExitSlipProgress the cards read, and on the same
 * summarizeSkillExitSlips, so the two surfaces cannot disagree: a skill on fire on the dashboard is
 * on fire here, with the same ticket count beside it. Only the layout differs — see
 * SkillHeaderExitSlipChip against DashboardCardExitSlipBadge.
 */

/**
 * Whether every exit ticket that infers this skill has been passed — the reading behind "Demonstrated
 * by Exit tickets", and the client's own answer to a question the server also answers through
 * SkillSubStates.INFERRED_VIA_EXITSLIP.
 *
 * Both are needed, and neither replaces the other: the inference runs in the backend after the fact,
 * so between a student finishing their last ticket and the Inference Engine recording it, this rule
 * is the only thing that knows. It is computed off a progress record built *with* the pending finish
 * (see buildSkillExitSlipProgress), which is what makes the window covered at all.
 *
 * The denominator is `relatedCount` rather than the tickets on hand, so a lesson whose ticket was
 * never assigned holds the verdict back — the same reading findSkillEarningExitSlipIds takes of an
 * outstanding lesson. A skill cannot be demonstrated while a ticket for it is unanswered, whoever's
 * fault that is.
 *
 * Takes the progress record alone, not the summary built from it: summarizeSkillExitSlips needs to be
 * told whether the skill is already earned, and this is one of the answers to that — asking it first
 * would be circular.
 */
export const isSkillDemonstratedByExitSlips = (progress: SkillExitSlipProgress): boolean =>
  progress.relatedCount > 0 &&
  progress.completedTickets.length === progress.relatedCount &&
  progress.completedTickets.every(ticket => ticket.scoreRatio >= EXIT_SLIP_PASSING_RATIO);

/**
 * How a skill won on exit tickets is named to the student, in the one place that wording lives.
 *
 * Read by the header's status badge (through useCurrentSkillResult, which maps the substate) and by
 * the Skill Challenge screen that replaces the challenge for such a skill. The two are side by side on
 * the same screen in the design, so they cannot be allowed to drift apart.
 */
export const EXIT_SLIP_DEMONSTRATED_LABEL = 'Demonstrated by Exit tickets';

/**
 * The chip's own words: "2 Exit tickets done" while some were only sat, "2 Exit tickets passed" once
 * every finished one is passed.
 *
 * The noun carries the distinction the unit header's badge tooltip has to spell out in a sentence —
 * that its number counts tickets *done*, not passed. Here there is room to say which is which, so the
 * chip says it.
 */
export const exitSlipChipLabel = (summary: SkillExitSlipBadgeSummary): string => {
  const noun = summary.completedCount === 1 ? 'Exit ticket' : 'Exit tickets';
  return `${summary.completedCount} ${noun} ${summary.taken.count === 0 ? 'passed' : 'done'}`;
};

/**
 * Which ephemeral message the skill page owes the student, or null for none.
 *
 * Two states earn one, each with its own monster:
 *  - 'on-fire'      — every ticket in and the skill still unearned, so the message points at the
 *                     Skill Challenge (exit-slip-monster-on-fire),
 *  - 'demonstrated' — the skill is already theirs through exit tickets (exit-slip-monster-normal).
 *
 * Everything else says nothing. A skill part-way through its tickets has the chip to report that, and
 * the message is a moment, not a status line — spending it on "you have done two tickets" would use it
 * up before either state the design actually draws arrives.
 *
 * The two cannot both apply, and not by the order of these branches: summarizeSkillExitSlips is told
 * whether the skill is already earned and puts the fire out when it is (see isSkillOnFire), so a
 * demonstrated skill reaches here with `isOnFire` already false.
 */
export type ExitSlipSkillMessageVariant = 'on-fire' | 'demonstrated';

export const resolveExitSlipSkillMessageVariant = (
  summary: SkillExitSlipBadgeSummary,
  isDemonstrated: boolean,
): ExitSlipSkillMessageVariant | null => {
  if (summary.isOnFire) {
    return 'on-fire';
  }
  return isDemonstrated ? 'demonstrated' : null;
};

/**
 * Where "already shown" is remembered, per student, per skill, and per variant.
 *
 * Keyed by variant on purpose: a skill the student is working through earns the progress message once,
 * and the day their last ticket lands it has something new to say — so the demonstrated message is not
 * swallowed by a progress message shown a week earlier. localStorage rather than sessionStorage
 * because "only for the first time" is meant to outlive the browser session, unlike the Revisit
 * message in SkillResourceHeader, which is per-session by design.
 *
 * Scoped by user id because a shared device is the normal case in a classroom.
 */
export const exitSlipSkillMessageKey = (
  userId: string | number,
  skillCode: string,
  variant: ExitSlipSkillMessageVariant,
): string => `exit_slip_skill_msg:${userId}:${skillCode}:${variant}`;

export const hasSeenExitSlipSkillMessage = (key: string): boolean => {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    // Storage unavailable only costs the message its "once": showing it again is the harmless
    // direction, the same trade-off writeDismissedExitSlipId takes.
    return false;
  }
};

export const markExitSlipSkillMessageSeen = (key: string): void => {
  try {
    localStorage.setItem(key, '1');
  } catch {
    // See above — nothing to recover from.
  }
};
